import React from 'react';
import { useTranslation } from 'react-i18next';
import { AttributeDataType } from '../types';

export interface AttributeFieldProps {
  name: string;
  dataType: AttributeDataType;
  value: string;
  options?: string[];
  unit?: string;
  isRequired?: boolean;
  error?: string;
  onChange: (value: string) => void;
}

/**
 * Renders the right input control for an EAV attribute based on its data type.
 * Values are always exchanged as strings; the backend validates typing.
 */
const AttributeField: React.FC<AttributeFieldProps> = ({
  name, dataType, value, options, unit, isRequired, error, onChange,
}) => {
  const { t } = useTranslation();
  const controlClass = `ims-form-control${error ? ' is-invalid' : ''}`;

  const renderControl = () => {
    switch (dataType) {
      case 'Number':
        return (
          <input
            type="number"
            step="1"
            className={controlClass}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case 'Decimal':
        return (
          <input
            type="number"
            step="any"
            className={controlClass}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case 'Boolean':
        return (
          <select
            className={controlClass}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">{t('attribute.notSet')}</option>
            <option value="true">{t('common.yes')}</option>
            <option value="false">{t('common.no')}</option>
          </select>
        );
      case 'Date':
        return (
          <input
            type="date"
            className={controlClass}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case 'Select':
        return (
          <select
            className={controlClass}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">{t('attribute.selectOption')}</option>
            {(options || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      default: // Text
        return (
          <input
            type="text"
            className={controlClass}
            value={value}
            maxLength={500}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };

  return (
    <div className="ims-form-group">
      <label className="ims-form-label">
        {name}
        {unit ? <span style={{ fontWeight: 400, color: 'var(--ims-text-muted)' }}> ({unit})</span> : null}
        {isRequired && <span className="required"> *</span>}
      </label>
      {renderControl()}
      {error && <span className="pi-field-error">{error}</span>}
    </div>
  );
};

export default AttributeField;
